import { skipToken } from '@apollo/client'
import { CircleCheck } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { H4 } from '@/components/sv/typographies'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { nodeVersions } from '@/config/static-sites'
import { useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { DETECT_FRAMEWORKS_QUERY } from '@/routes/static-sites/create-static-site/gql'
import type { CreateStaticSiteFormValues } from '@/routes/static-sites/create-static-site/form/schema'
import type {
  DetectFrameworkQuery,
  DetectFrameworkQueryVariables,
} from '@/routes/static-sites/create-static-site/gql.types.gen'

export const BuildSettings = () => {
  const form = useFormContext<CreateStaticSiteFormValues>()
  const idRemoteRepository = form.watch('idRemoteRepository')
  const gitType = form.watch('gitType')
  const branch = form.watch('defaultBranch')
  const buildEnabled = form.watch('buildEnabled')

  const { data } = useSuspenseQuery<
    DetectFrameworkQuery,
    DetectFrameworkQueryVariables
  >(
    DETECT_FRAMEWORKS_QUERY,
    idRemoteRepository && branch && gitType
      ? { variables: { idRemoteRepository, gitType, branch } }
      : skipToken
  )

  const framework = useMemo(() => data?.detectFrameworkForStaticSite, [data])

  useEffect(() => {
    if (
      framework &&
      framework.buildCommand &&
      framework.publishDirectory !== form.getValues('publishedDirectory')
    ) {
      form.setValue('buildEnabled', true)
      form.setValue('buildCommand', framework.buildCommand)
      form.setValue('publishedDirectory', framework.publishDirectory)
      form.setValue('nodeVersion', framework.nodeVersion ?? 'lts')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framework])

  useEffect(() => {
    if (!buildEnabled) {
      form.setValue('nodeVersion', null)
      form.setValue('buildCommand', undefined)
    }
  }, [form, buildEnabled])

  return (
    <>
      <div className="flex items-center justify-between">
        <H4>Build settings</H4>
        {framework?.name ? (
          <div className="flex items-center gap-1">
            <CircleCheck
              className={cn('size-4 fill-emerald-600 stroke-background')}
            />
            <div className="leading-4.5 text-sm text-muted-foreground">
              Framework detected: {framework.name}
            </div>
          </div>
        ) : null}
      </div>
      <FormField
        control={form.control}
        name="buildEnabled"
        render={({ field: switchField }) => (
          <>
            <div className="flex items-center justify-between">
              <FormItem className="flex items-center gap-2">
                <FormControl>
                  <Switch
                    defaultChecked={Boolean(switchField.value)}
                    checked={Boolean(switchField.value)}
                    onCheckedChange={switchField.onChange}
                  />
                </FormControl>
                <FormLabel>Build site before publishing</FormLabel>
              </FormItem>
            </div>
            <div className="grid grid-cols-2 items-start gap-4">
              <FormField
                control={form.control}
                name="nodeVersion"
                render={({ field: { value, onChange, ...field } }) => (
                  <FormItem>
                    <FormLabel>Node version</FormLabel>
                    <FormControl>
                      <Select
                        disabled={!switchField.value}
                        value={value ?? ''}
                        onValueChange={onChange}
                        {...field}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a node version" />
                        </SelectTrigger>
                        <SelectContent>
                          {nodeVersions.map((version) => (
                            <SelectItem
                              value={version.value}
                              key={version.value}
                            >
                              {version.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="buildCommand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Build command</FormLabel>
                    <FormControl>
                      <Input
                        disabled={!switchField.value}
                        autoComplete="off"
                        aria-label="Build command"
                        placeholder="npm run build"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </>
        )}
      />
      <div className="grid grid-cols-2 items-start gap-4">
        <FormField
          control={form.control}
          name="rootDirectory"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Root directory</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  aria-label="Root directory"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="publishedDirectory"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Publish directory</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  aria-label="Publish directory"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="grid grid-cols-2 items-start gap-4">
        <FormField
          control={form.control}
          name="indexFile"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Index file</FormLabel>
              <FormControl>
                <Input autoComplete="off" aria-label="Index file" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="errorFile"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Error file</FormLabel>
              <FormControl>
                <Input autoComplete="off" aria-label="Error file" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  )
}
